import { refreshTokenApi } from "@/api/services/main/auth";
import { isNetworkError } from "@/utils/error";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { PRIVACY_CONSENT_APPROVED_KEY, PRIVACY_CONSENT_APPROVED_VALUE } from "@/constants/common";
import { AppRoutes, ClientRoutes } from "@/routes/config";
import { getLogoutUrl, getLoginUrl, isAppUpdateRequiredOnRefresh, logout, redirectToStoreForWebAccess, setAxiosAuth } from "@/utils/api";
import { toast } from "@/utils/toast";
import { verifyToken } from "@/utils/token";
import Cookies from "js-cookie";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

const LOCAL_STORAGE_APP_UPDATED_KEY = "app-updated";

export type WithAuthInjectedProps = {
  isLoading?: boolean;
};

type WithAuthOptions = {
  /** When true, passes `isLoading` to the wrapped component instead of blocking render with LoadingScreen */
  delegateLoading?: boolean;
};

export const withAuth: <CP extends {}>(WrappedComponent: FC<CP & WithAuthInjectedProps>, options?: WithAuthOptions) => FC<CP> =
  (WrappedComponent, options) => (props) => {
    const { pathname, search } = useLocation();
    const { t } = useTranslation();
    const [isVerified, setIsVerified] = useState(isAuthenticated);
    const userIdRef = useRef(Cookies.get("learner-user-id"));
    const learnerAccountIdRef = useRef(Cookies.get("learner-account-id"));
    const navigate = useNavigate();

    useEffect(() => {
      if (localStorage.getItem(LOCAL_STORAGE_APP_UPDATED_KEY) === "1") {
        localStorage.removeItem(LOCAL_STORAGE_APP_UPDATED_KEY);
        toast.success(t("common.appUpdated"));
      }
    }, [t]);

    useEffect(() => {
      const interval = setInterval(() => {
        if (userIdRef.current && userIdRef.current != Cookies.get("learner-user-id")) {
          const oldUrl = `${location.pathname}${location.search}`;
          location.href = getLogoutUrl({ redirectUrl: oldUrl, learnerId: learnerAccountIdRef.current });
        }
      }, 2000);
      return () => {
        clearInterval(interval);
      };
    }, []);

    useEffect(() => {
      // Guards against stale async work: if pathname changes (effect re-runs / component unmounts)
      // while verifyAuth/checkVersion are in flight, `ignore` stops them writing state or reloading
      // the page after the route already moved on.
      let ignore = false;

      // Returns whether it is SAFE to trigger a version reload afterwards:
      // true  = session settled healthily (valid/refreshed token, or a public route)
      // false = degraded network-kept state, or we redirected to login — do NOT reload in these cases.
      const verifyAuth = async (): Promise<boolean> => {
        setAxiosAuth();
        const authenticated = isAuthenticated();

        // If not authenticated, try refresh before redirecting to login
        if (!authenticated && ClientRoutes.some((path) => pathname.startsWith(path))) {
          const refreshToken = Cookies.get("learner-refresh-token");
          const accountId = Cookies.get("learner-account-id");

          if (refreshToken && accountId) {
            try {
              await refreshTokenApi({ refreshToken, accountId });
              if (ignore) return false; // route changed while refreshing — drop stale write
              setAxiosAuth();
              setIsVerified(true);
              return true; // refreshed cleanly → safe to run the version reload afterwards
            } catch (refreshError) {
              if (ignore) return false; // route changed while refreshing — drop stale write
              // A transient network failure (e.g. app just resumed from background, network not ready
              // yet) is NOT a sign the session is invalid — the request never reached the server. Keep
              // the still-valid cookies and let a later API call refresh once connectivity returns.
              // Only a genuine server auth rejection falls through to logout.
              if (isNetworkError(refreshError)) {
                setAxiosAuth();
                setIsVerified(true);
                return false; // degraded network — keep session but do NOT reload into a flaky network
              }
              if (isAppUpdateRequiredOnRefresh(refreshError)) {
                redirectToStoreForWebAccess();
                return false;
              }
            }
          }

          // Clear stale session and navigate to login directly (no /logout roundtrip)
          const redirectUrl = `${pathname}${search}`;
          const learnerId = learnerAccountIdRef.current;
          logout();
          navigate(getLoginUrl(redirectUrl, learnerId), { replace: true });
          return false; // navigating to login — don't trigger a reload
        }

        setIsVerified(true);
        return true; // already authenticated / public route — safe
      };

      // check version build
      const checkVersion = async () => {
        try {
          const response = await fetch("/version.json", {
            headers: { "Cache-Control": "no-cache" },
          });
          const { version } = await response.json();
          if (ignore) return; // route changed — don't reload the page the user just navigated to
          const currentVersion = localStorage.getItem("app-version");

          // show modal and update version
          if (version && (!currentVersion || version !== parseInt(currentVersion))) {
            localStorage.setItem("app-version", version);
            localStorage.setItem(LOCAL_STORAGE_APP_UPDATED_KEY, "1");
            window.location.reload();
          }
        } catch (error) {
          console.error("Error checking version:", error);
        }
      };

      // Run the version check ONLY after auth settles, and only when it's safe — session healthy
      // (valid/refreshed token, not a degraded network-kept state or a redirect to login) and online.
      // This prevents the reload from racing with / aborting an in-flight token refresh, which
      // surfaced as ERR_NETWORK and forced a logout.
      (async () => {
        const safeToReload = await verifyAuth();
        if (ignore) return; // route changed — abandon the stale chain
        if (safeToReload && navigator.onLine) {
          await checkVersion();
        }
      })();

      return () => {
        ignore = true;
      };
    }, [pathname, navigate]);

    // Default: block render until verified
    if (!options?.delegateLoading && !isVerified) return <LoadingScreen />;

    // Check privacy consent before rendering the inner components
    const isPrivacyConsentApproved = localStorage.getItem(PRIVACY_CONSENT_APPROVED_KEY) === PRIVACY_CONSENT_APPROVED_VALUE;
    if (!isPrivacyConsentApproved) {
      const redirectUrl = `${pathname}${search}`;
      const params = new URLSearchParams({ redirectUrl });
      return <Navigate to={`${AppRoutes.privacyConsent}?${params.toString()}`} replace />;
    }

    return <WrappedComponent {...props} isLoading={options?.delegateLoading ? !isVerified : undefined} />;
  };

function isAuthenticated(): boolean {
  const token = Cookies.get("learner-token");
  return !!token && verifyToken(token);
}
